const OCR_UPLOAD_TARGET_BYTES = 5 * 1024 * 1024;
const OCR_UPLOAD_LONG_EDGE = 2200;
const OCR_UPLOAD_JPEG_QUALITY = 0.86;

const isBrowserImageCompressionAvailable = (): boolean => {
  return typeof document !== "undefined" && typeof Image !== "undefined" && typeof URL !== "undefined";
};

const loadImage = (file: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    const url = URL.createObjectURL(file);
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not load image for upload preparation."));
    };
    image.src = url;
  });

export const shouldPrepareOcrImageUpload = (file: File): boolean => {
  return file.type.startsWith("image/") && file.type !== "image/webp" && file.size > OCR_UPLOAD_TARGET_BYTES;
};

export async function prepareOcrUploadFile(file: File): Promise<File> {
  if (!shouldPrepareOcrImageUpload(file) || !isBrowserImageCompressionAvailable()) {
    return file;
  }

  try {
    const image = await loadImage(file);
    const scale = Math.min(1, OCR_UPLOAD_LONG_EDGE / Math.max(image.naturalWidth || image.width, image.naturalHeight || image.height));
    const width = Math.max(1, Math.round((image.naturalWidth || image.width) * scale));
    const height = Math.max(1, Math.round((image.naturalHeight || image.height) * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) {
      return file;
    }
    context.drawImage(image, 0, 0, width, height);
    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, "image/jpeg", OCR_UPLOAD_JPEG_QUALITY);
    });
    if (!blob || blob.size >= file.size) {
      return file;
    }
    const baseName = file.name.replace(/\.[^.]+$/, "") || "citation";
    return new File([blob], `${baseName}.jpg`, { type: "image/jpeg", lastModified: Date.now() });
  } catch {
    return file;
  }
}
